import React, { useState, useEffect } from 'react';
import { Popover, Select, InputNumber, Button } from 'antd';
import { FilterOutlined, FilterFilled } from '@ant-design/icons';
import { colorConstants } from 'config/colorConstants';

const OPERATOR_LABELS: Record<string, string> = {
    eq: 'Equal to',
    lt: 'Less than',
    gt: 'Greater than',
};

export interface KpiFilterValue {
    operator: string;
    value: number;
}

interface KpiColumnFilterPopoverProps {
    kpiKey: string;
    filterOperators: string[];
    activeFilter?: KpiFilterValue;
    onApply: (kpiKey: string, filter: KpiFilterValue | null) => void;
}

const KpiColumnFilterPopover: React.FC<KpiColumnFilterPopoverProps> = ({
    kpiKey,
    filterOperators,
    activeFilter,
    onApply,
}) => {
    const [open, setOpen] = useState(false);
    const [selectedOperator, setSelectedOperator] = useState<string | undefined>(activeFilter?.operator);
    const [inputValue, setInputValue] = useState<number | null>(activeFilter?.value ?? null);

    useEffect(() => {
        setSelectedOperator(activeFilter?.operator);
        setInputValue(activeFilter?.value ?? null);
    }, [activeFilter]);

    const handleApply = () => {
        if (selectedOperator && inputValue !== null) {
            onApply(kpiKey, { operator: selectedOperator, value: inputValue });
            setOpen(false);
        }
    };

    const handleClear = () => {
        setSelectedOperator(undefined);
        setInputValue(null);
        onApply(kpiKey, null);
        setOpen(false);
    };

    const operatorOptions = filterOperators.map((op) => ({
        label: OPERATOR_LABELS[op] ?? op,
        value: op,
    }));

    const isActive = !!activeFilter;
    const isApplyDisabled = !selectedOperator || inputValue === null;

    const content = (
        <div style={{ width: 220 }} onClick={(e) => e.stopPropagation()}>
            <div className="mb-2">
                <label className="block mb-1" style={{ fontSize: 12, fontWeight: 500, color: colorConstants.fontGrey }}>
                    Condition
                </label>
                <Select
                    value={selectedOperator}
                    onChange={setSelectedOperator}
                    options={operatorOptions}
                    placeholder="Select condition"
                    style={{ width: '100%' }}
                    size="middle"
                />
            </div>
            <div className="mb-3">
                <label className="block mb-1" style={{ fontSize: 12, fontWeight: 500, color: colorConstants.fontGrey }}>
                    Value
                </label>
                <InputNumber
                    value={inputValue}
                    onChange={(val) => setInputValue(val)}
                    placeholder="Enter numeric value"
                    style={{ width: '100%' }}
                    size="middle"
                />
            </div>
            <div
                className="flex justify-end items-center gap-2"
                style={{ borderTop: `1px solid ${colorConstants.borderGreyAlt}`, paddingTop: 10, marginTop: 4 }}
            >
                <Button
                    size="middle"
                    onClick={handleClear}
                    disabled={!isActive}
                    style={{ borderRadius: 4, fontSize: 12, padding: '2px 14px' }}
                >
                    Clear
                </Button>
                <Button
                    size="middle"
                    onClick={handleApply}
                    disabled={isApplyDisabled}
                    style={{
                        borderRadius: 4,
                        fontSize: 12,
                        padding: '2px 14px',
                        background: isApplyDisabled ? colorConstants.greyAlt6 : colorConstants.violet,
                        borderColor: isApplyDisabled ? colorConstants.greyAlt : colorConstants.violet,
                        color: isApplyDisabled ? colorConstants.greyAlt17 : '#fff',
                    }}
                >
                    Apply
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            content={content}
            title={<span style={{ fontSize: 13, fontWeight: 600, color: colorConstants.fontGrey }}>Filter Column</span>}
            trigger="click"
            open={open}
            onOpenChange={(visible) => setOpen(visible)}
            placement="bottomRight"
            overlayInnerStyle={{ borderRadius: 6, padding: '10px 12px' }}
        >
            <span
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(!open);
                }}
                className="cursor-pointer ml-1"
                style={{ color: isActive ? colorConstants.violet : colorConstants.greyAlt4, position: 'relative' }}
            >
                {isActive ? <FilterFilled style={{ fontSize: 12 }} /> : <FilterOutlined style={{ fontSize: 12 }} />}
                {isActive && (
                    <span
                        style={{
                            position: 'absolute',
                            top: -3,
                            right: -4,
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: colorConstants.violet,
                        }}
                    />
                )}
            </span>
        </Popover>
    );
};

export default KpiColumnFilterPopover;
